import {createFeatureSelector,  createSelector} from "@ngrx/store";
import {IFlattenedCategories, IGetCategoriesItemDto} from "../../main/pages/categories.dto";
import {flattenCategories} from "../../main/pages/categories.utils";
import {ICategoriesState} from "../reducers/categories.reducer";
import * as storeKeys from './../store-key.config';

export const categoriesFeatureSelector = createFeatureSelector<ICategoriesState>(storeKeys.categories);
export const flattenedCategories = createSelector(categoriesFeatureSelector, state=> flattenCategories(state.categories ?? []));
export const allCategories = createSelector(categoriesFeatureSelector, state=> state.categories);
export const getCategoryNameById = (categoryId: number) => createSelector(categoriesFeatureSelector, (state) => flattenCategories(state.categories ?? []).find(x => x.id == categoryId));
export const getParentCategoriesIds = (categoryIds: number | number[]) => createSelector(categoriesFeatureSelector, (state) => {
    const flattenCategoriesList = flattenCategories(state.categories ?? []);
    const ids = Array.isArray(categoryIds) ? categoryIds : [categoryIds];
    const parentIdsSet = new Set<number>();

    ids.forEach(id => {
        const category = flattenCategoriesList.find(x => x.id === id);
        if (category && category.parent_category > 0) {
            collectParentCategories(category.parent_category, flattenCategoriesList).forEach(parentId => {
                parentIdsSet.add(parentId);
            });
        }
    });

    return Array.from(parentIdsSet);
});

/**
 * Recursively collects all parent category IDs for a given category.
 * Includes cycle detection to prevent infinite loops in case of circular references.
 * 
 * @param parentId - The ID of the parent category to start from
 * @param categories - Flattened list of all categories
 * @param visited - Set of already visited category IDs to detect cycles (internal use)
 * @returns Array of all parent category IDs in the hierarchy
 */
function collectParentCategories(
    parentId: number, 
    categories: IFlattenedCategories[], 
    visited: Set<number> = new Set()
): number[] {
    // Base case: invalid parent ID or already visited (cycle detection)
    if (parentId <= 0 || visited.has(parentId)) {
        return [];
    }
    
    // Mark this category as visited to prevent cycles
    visited.add(parentId);
    
    // Add the current parentId to results
    const parent = [parentId];
    
    // Find the next parent in the hierarchy
    const nextParentId = categories.find(x => x.id === parentId)?.parent_category;
    
    // Recursively collect parent categories
    return [...parent, ...collectParentCategories(nextParentId, categories, visited)];
}

